/**
 * Custom hook for subscribing to posting queue updates
 *
 * Uses Firestore real-time listeners for queue metadata and comments.
 * VM status (from GCP API) is polled every 30s only when queue is running.
 * Falls back to REST API polling if Firestore fails.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PendingComment, PostingQueueStatus, api } from '../services/api';
import {
    subscribeToPostingQueueMeta,
    subscribeToPostingQueueComments,
    PostingQueueMeta,
} from '../services/firestoreListeners';
import { isFirebaseInitialized } from '../config/firebase';

interface UsePostingQueueSubscriptionResult {
    /** Queue metadata from Firestore (isRunning, status, vncUrl) */
    queueMeta: PostingQueueMeta | null;
    /** Pending comments from Firestore */
    pendingComments: PendingComment[];
    /** VM status from GCP API (polled when queue running) */
    vmStatus: string | null;
    /** Combined queue status for UI (merges Firestore + API data) */
    combinedStatus: PostingQueueStatus | null;
    /** Whether using real-time Firestore listeners */
    isUsingRealtime: boolean;
    /** Whether currently loading */
    loading: boolean;
    /** Error message if any */
    error: string | null;
    /** Manually refresh data */
    refresh: () => void;
}

interface UsePostingQueueSubscriptionOptions {
    /** Enable polling even when Firestore is working (for debugging) */
    forcePolling?: boolean;
    /** VM status polling interval in milliseconds (default: 30000) */
    vmPollInterval?: number;
    /** Full polling interval in milliseconds when Firestore fails (default: 10000) */
    fullPollInterval?: number;
    /** Limit of comments to subscribe to (default: 100) */
    commentsLimit?: number;
}

/**
 * Hook to subscribe to posting queue updates with Firestore real-time listeners
 * Falls back to REST API polling if Firestore fails
 *
 * @param accountId - Encoded account ID to subscribe to
 * @param options - Configuration options
 */
export function usePostingQueueSubscription(
    accountId: string | undefined,
    options: UsePostingQueueSubscriptionOptions = {}
): UsePostingQueueSubscriptionResult {
    const {
        forcePolling = false,
        vmPollInterval = 30000,  // 30 seconds
        fullPollInterval = 10000,  // 10 seconds for fallback
        commentsLimit = 100,
    } = options;

    const [queueMeta, setQueueMeta] = useState<PostingQueueMeta | null>(null);
    const [pendingComments, setPendingComments] = useState<PendingComment[]>([]);
    const [vmStatus, setVmStatus] = useState<string | null>(null);
    const [combinedStatus, setCombinedStatus] = useState<PostingQueueStatus | null>(null);
    const [isUsingRealtime, setIsUsingRealtime] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Unsubscribe functions for cleanup
    const metaUnsubRef = useRef<(() => void) | null>(null);
    const commentsUnsubRef = useRef<(() => void) | null>(null);

    // Polling timer refs
    const vmPollTimerRef = useRef<NodeJS.Timeout | null>(null);
    const fullPollTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Track if queue is running for VM polling
    const isQueueRunningRef = useRef(false);

    // REST API fallback: fetch full queue status
    const fetchViaRestApi = useCallback(async (sync: boolean = false) => {
        if (!accountId) return;

        try {
            const [status, preview] = await Promise.all([
                api.getPostingQueueStatus(accountId, sync),
                api.getPostingQueuePreview(accountId, commentsLimit),
            ]);

            setCombinedStatus(status);
            setVmStatus(status.vmStatus || null);
            setPendingComments(preview.comments);
            setError(null);
            setLoading(false);
        } catch (err) {
            console.error('[usePostingQueueSubscription] REST API fetch error:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch queue data');
            setLoading(false);
        }
    }, [accountId, commentsLimit]);

    // Poll VM status only (used when Firestore is active)
    const pollVmStatus = useCallback(async () => {
        if (!accountId || !isQueueRunningRef.current) return;

        try {
            const status = await api.getPostingQueueStatus(accountId, false);
            setVmStatus(status.vmStatus || null);

            // Update combined status with fresh VM status
            setCombinedStatus(prev => {
                if (!prev) return status;
                return {
                    ...prev,
                    vmStatus: status.vmStatus,
                    vmInstanceName: status.vmInstanceName,
                };
            });
        } catch (err) {
            console.error('[usePostingQueueSubscription] VM status poll error:', err);
        }
    }, [accountId]);

    // Start VM status polling (only when queue is running)
    const startVmPolling = useCallback(() => {
        if (vmPollTimerRef.current) {
            clearInterval(vmPollTimerRef.current);
        }

        if (!isQueueRunningRef.current) {
            console.log('[usePostingQueueSubscription] Queue not running, skipping VM polling');
            return;
        }

        console.log('[usePostingQueueSubscription] Starting VM status polling (30s interval)');
        pollVmStatus();  // Initial fetch
        vmPollTimerRef.current = setInterval(pollVmStatus, vmPollInterval);
    }, [pollVmStatus, vmPollInterval]);

    // Stop VM polling
    const stopVmPolling = useCallback(() => {
        if (vmPollTimerRef.current) {
            clearInterval(vmPollTimerRef.current);
            vmPollTimerRef.current = null;
        }
    }, []);

    // Start full polling (fallback when Firestore fails)
    const startFullPolling = useCallback(() => {
        if (fullPollTimerRef.current) {
            clearInterval(fullPollTimerRef.current);
        }

        console.log('[usePostingQueueSubscription] Starting full REST API polling');
        fetchViaRestApi(true);  // Initial fetch with sync
        fullPollTimerRef.current = setInterval(() => fetchViaRestApi(false), fullPollInterval);
    }, [fetchViaRestApi, fullPollInterval]);

    // Stop full polling
    const stopFullPolling = useCallback(() => {
        if (fullPollTimerRef.current) {
            clearInterval(fullPollTimerRef.current);
            fullPollTimerRef.current = null;
        }
    }, []);

    // Setup Firestore listeners
    const setupFirestoreListeners = useCallback(() => {
        if (!accountId) return;

        console.log('[usePostingQueueSubscription] Setting up Firestore listeners for:', accountId);

        // Subscribe to queue metadata
        metaUnsubRef.current = subscribeToPostingQueueMeta(
            accountId,
            (meta) => {
                setQueueMeta(meta);
                setIsUsingRealtime(true);
                setLoading(false);
                setError(null);

                // Track if queue is running for VM polling
                const wasRunning = isQueueRunningRef.current;
                isQueueRunningRef.current = meta?.isPostingQueueRunning ?? false;

                // Update combined status with Firestore data
                setCombinedStatus(prev => ({
                    accountId: accountId,
                    status: meta?.status || 'idle',
                    pendingCount: prev?.pendingCount ?? 0,
                    vmStatus: prev?.vmStatus,
                    vncUrl: meta?.vncUrl,
                    vmInstanceName: meta?.vmInstanceName,
                    isPostingQueueRunning: meta?.isPostingQueueRunning,
                    needsLoginSince: meta?.needsLoginSince,
                }));

                // Start/stop VM polling based on queue running state
                if (isQueueRunningRef.current && !wasRunning) {
                    startVmPolling();
                } else if (!isQueueRunningRef.current && wasRunning) {
                    stopVmPolling();
                }
            },
            (err) => {
                console.error('[usePostingQueueSubscription] Queue meta listener error:', err);
                setIsUsingRealtime(false);
                setError('Firestore connection failed, using polling');
                startFullPolling();
            }
        );

        // Subscribe to pending comments
        commentsUnsubRef.current = subscribeToPostingQueueComments(
            accountId,
            (comments) => {
                setPendingComments(comments);

                // Update pending count in combined status
                setCombinedStatus(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        pendingCount: comments.length,
                    };
                });
            },
            (err) => {
                console.error('[usePostingQueueSubscription] Queue comments listener error:', err);
                // Don't fall back here since meta listener handles fallback
            },
            commentsLimit
        );
    }, [accountId, commentsLimit, startVmPolling, stopVmPolling, startFullPolling]);

    // Cleanup function
    const cleanup = useCallback(() => {
        stopVmPolling();
        stopFullPolling();

        if (metaUnsubRef.current) {
            metaUnsubRef.current();
            metaUnsubRef.current = null;
        }
        if (commentsUnsubRef.current) {
            commentsUnsubRef.current();
            commentsUnsubRef.current = null;
        }
    }, [stopVmPolling, stopFullPolling]);

    // Manual refresh
    const refresh = useCallback(() => {
        if (isUsingRealtime) {
            // If using real-time, just trigger VM status poll
            pollVmStatus();
        } else {
            // If using polling, fetch fresh data
            fetchViaRestApi(true);
        }
    }, [isUsingRealtime, pollVmStatus, fetchViaRestApi]);

    // Main effect to setup listeners
    useEffect(() => {
        if (!accountId) {
            setLoading(false);
            return;
        }

        // Reset state for new account
        setLoading(true);
        setQueueMeta(null);
        setPendingComments([]);
        setVmStatus(null);
        setCombinedStatus(null);
        setError(null);
        setIsUsingRealtime(false);
        isQueueRunningRef.current = false;

        // Check if we should use Firestore or polling
        if (forcePolling || !isFirebaseInitialized()) {
            console.log('[usePostingQueueSubscription] Using REST API polling');
            startFullPolling();
        } else {
            console.log('[usePostingQueueSubscription] Setting up Firestore real-time listeners');
            setupFirestoreListeners();
        }

        return cleanup;
    }, [accountId, forcePolling, setupFirestoreListeners, startFullPolling, cleanup]);

    return {
        queueMeta,
        pendingComments,
        vmStatus,
        combinedStatus,
        isUsingRealtime,
        loading,
        error,
        refresh,
    };
}
